/**
 * SupportBoardSection — White-Label → Support: the monday board this
 * organization's support reports are ALSO sent to, by CorpBrain itself
 * (PanLuma keeps receiving every report as today). Super admin only; the
 * backend enforces it.
 *
 * Top to bottom: "What happens" — one sentence that follows the choice being
 * made, with the board as a chip; the choice as three cards (the organization
 * above's, its own board, not sent); for its own board, the monday connection
 * and the board (MondayBoardSetup: create one, or use an existing one and
 * check it); the organizations under this one and what each uses; delivery —
 * a test report and the last delivery (SupportDelivery).
 *
 * Saving is the editor's bottom bar: the section reports its state up
 * (`onStateChange`) and the bar calls `save` / `discard` through the ref —
 * one Save on the screen, as for the brand fields.
 *
 * The rule and the signing live on the server (backend/modules/support_board);
 * `supportBoardModel` only words what it decided.
 */
import {
  type KeyboardEvent, type ReactNode, forwardRef, useEffect, useId, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Input, Spin, message as antdMessage } from 'antd';
import {
  ApartmentOutlined, CheckCircleFilled, ExclamationCircleFilled, ExportOutlined,
  InfoCircleOutlined, LeftOutlined, ProjectOutlined, RightOutlined, StopOutlined,
} from '@ant-design/icons';
import MondayBoardSetup from './MondayBoardSetup';
import SupportDelivery from './SupportDelivery';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing/paths';
import { bidiIsolate } from '@/utils/bidi';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import {
  type BoardChip, type Draft, type Mode, type Summary, type SupportView,
  belowRows, cardsFor, checkBoardLink, draftOf, isDirty, summarize,
} from './supportBoardModel';

export interface SupportBoardState {
  dirty: boolean;
  canSave: boolean;
  saving: boolean;
  /** This organization made a choice of its own (the rail's dot). */
  configured: boolean;
  /** There is a choice to make here (loaded, and not the platform root). */
  editable: boolean;
}

export interface SupportBoardHandle {
  save: () => Promise<void>;
  discard: () => void;
}

interface Props {
  tenantId: string;
  /** The organization's brand name — a board CorpBrain creates is named after it. */
  brandName: string;
  onStateChange?: (state: SupportBoardState) => void;
}

const P = 'whiteLabel.supportBoard';

const SupportBoardSection = forwardRef<SupportBoardHandle, Props>(function SupportBoardSection(
  { tenantId, brandName, onStateChange }, ref,
) {
  const { t, direction, language } = useLanguage();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const uid = useId();
  const id = (name: string) => `${uid}-${name}`;
  const [view, setView] = useState<SupportView | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<{ code: string; field?: string } | null>(null);
  const [linkTouched, setLinkTouched] = useState(false);
  const cardRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    let live = true;
    setView(null);
    setDraft(null);
    setLoadError(false);
    api.get(`/super-admin/tenants/${tenantId}/support-board`)
      .then(res => { if (live) { setView(res.data); setDraft(draftOf(res.data)); } })
      .catch(() => { if (live) setLoadError(true); });
    return () => { live = false; };
  }, [tenantId]);

  const link = useMemo(() => checkBoardLink(draft?.board_link ?? ''), [draft?.board_link]);
  const dirty = !!view && !!draft && isDirty(view, draft);
  const canSave = dirty && !saving && (draft?.mode !== 'board' || link.state === 'ok');
  const configured = !!view && view.mode !== 'inherit';
  const editable = !!view && !view.is_platform_root;

  useEffect(() => {
    onStateChange?.({ dirty, canSave, saving, configured, editable });
  }, [dirty, canSave, saving, configured, editable, onStateChange]);

  const save = async () => {
    if (!view || !draft || !canSave) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/super-admin/tenants/${tenantId}/support-board`, {
        mode: draft.mode,
        ...(draft.mode === 'board' ? { board_link: draft.board_link.trim(), board_name: draft.board_name.trim() } : {}),
      });
      setView(res.data);
      setDraft(draftOf(res.data));
      setLinkTouched(false);
      antdMessage.success(t(`${P}.saved`));
    } catch (err) {
      const data = (err as { response?: { data?: { error_code?: string; field?: string } } })?.response?.data;
      setError({ code: data?.error_code || 'generic', field: data?.field });
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!view) return;
    setDraft(draftOf(view));
    setError(null);
    setLinkTouched(false);
  };

  useImperativeHandle(ref, () => ({ save, discard }));

  if (loadError) {
    return <div className="wl-field-hint" role="alert">{t(`${P}.loadFailed`)}</div>;
  }
  if (!view || !draft) {
    return <div className="wl-email-sending-loading"><Spin /></div>;
  }

  const set = (patch: Partial<Draft>) => { setDraft({ ...draft, ...patch }); setError(null); };
  const applyView = (next: SupportView, boardChanged = false) => {
    setView(next);
    if (boardChanged || !isDirty(view, draft)) {
      setDraft(draftOf(next));
      setLinkTouched(false);
    }
  };
  const openOrg = (orgId: string) => navigate(tenantPath(`/super-admin/tenants/${orgId}/branding#support`));
  const org = view.tenant_name;
  const summary = summarize(view, draft);
  const { follow, offValue } = cardsFor(view);
  const below = belowRows(view, new Intl.Collator(language));
  const boardText = (b: BoardChip) => b.name || t(`${P}.boardNumber`, { id: b.id });

  // ── The cards ──
  const inherited = view.inherited;
  const followLine = inherited
    ? t(`${P}.card.followBoard`, {
      board: bidiIsolate(boardText({ name: inherited.board_name, id: inherited.board_id, url: null })),
      source: bidiIsolate(inherited.source_tenant_name),
    })
    : view.inherited_off_by
      ? t(`${P}.card.followOff`, { org: bidiIsolate(view.inherited_off_by) })
      : t(`${P}.card.followNothing`);
  const cards: Array<{ value: Mode; icon: ReactNode; title: string; line: string; testId: string }> = [
    ...(follow ? [{
      value: 'inherit' as Mode, icon: <ApartmentOutlined />, testId: 'inherit',
      title: t(`${P}.card.followTitle`, { org: bidiIsolate(view.parent_name ?? '') }), line: followLine,
    }] : []),
    { value: 'board', icon: <ProjectOutlined />, testId: 'board',
      title: t(`${P}.card.ownTitle`), line: t(`${P}.card.ownLine`) },
    { value: offValue, icon: <StopOutlined />, testId: 'off',
      title: t(`${P}.card.offTitle`), line: t(follow ? `${P}.card.offLine` : `${P}.card.offLineTop`) },
  ];
  const selected = Math.max(0, cards.findIndex(c => c.value === draft.mode));

  const choose = (index: number) => {
    const card = cards[index];
    if (!card) return;
    set({ mode: card.value });
    cardRefs.current[index]?.focus();
  };
  const onCardKey = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const forward = direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const back = direction === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
    let next: number | null = null;
    if (e.key === 'ArrowDown' || e.key === forward) next = (index + 1) % cards.length;
    else if (e.key === 'ArrowUp' || e.key === back) next = (index - 1 + cards.length) % cards.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = cards.length - 1;
    if (next === null) return;
    e.preventDefault();
    choose(next);
  };

  const linkInvalid = draft.mode === 'board' && linkTouched && link.state === 'error';
  const serverLinkError = error?.field === 'board_link';
  const Chevron = direction === 'rtl' ? LeftOutlined : RightOutlined;

  // "Use an existing board": its link and name, saved with the footer.
  const existingFields = (
    <div className="wl-sb-board-fields" data-testid="wl-support-board-fields">
      <div className="wl-field">
        <label className="wl-field-label" htmlFor={id('link')}>{t(`${P}.boardLink`)}</label>
        <Input
          id={id('link')}
          dir="ltr"
          value={draft.board_link}
          onChange={e => set({ board_link: e.target.value })}
          onBlur={() => setLinkTouched(true)}
          placeholder="https://your-team.monday.com/boards/1234567890"
          status={linkInvalid || serverLinkError ? 'error' : undefined}
          aria-invalid={linkInvalid || serverLinkError}
          aria-describedby={`${id('link-hint')} ${id('link-state')}`}
          data-testid="wl-support-board-link"
        />
        <div className="wl-field-hint" id={id('link-hint')}>{t(`${P}.boardLinkHint`)}</div>
        <div id={id('link-state')} aria-live="polite">
          {link.state === 'ok' && (
            <div className="wl-sb-link-state wl-sb-link-state--ok" data-testid="wl-support-board-link-ok">
              <CheckCircleFilled aria-hidden />
              <span>{t(`${P}.linkOk`, { id: link.boardId, account: link.account })}</span>
            </div>
          )}
          {linkInvalid && link.state === 'error' && (
            <div className="wl-sb-link-state wl-sb-link-state--error" data-testid="wl-support-board-link-error">
              <ExclamationCircleFilled aria-hidden />
              <span>{t(`${P}.linkErrors.${link.code}`)}</span>
            </div>
          )}
        </div>
      </div>
      <div className="wl-field">
        <label className="wl-field-label" htmlFor={id('name')}>{t(`${P}.boardName`)}</label>
        <Input
          id={id('name')}
          dir="auto"
          value={draft.board_name}
          maxLength={120}
          onChange={e => set({ board_name: e.target.value })}
          placeholder={t(`${P}.boardNamePlaceholder`)}
          status={error?.field === 'board_name' ? 'error' : undefined}
          aria-describedby={id('name-hint')}
          data-testid="wl-support-board-name"
        />
        <div className="wl-field-hint" id={id('name-hint')}>{t(`${P}.boardNameHint`)}</div>
      </div>
    </div>
  );

  return (
    <section className="wl-section wl-sb" data-testid="wl-support-board">
      <header className="wl-section-header">
        <h4 className="wl-section-title">{t(`${P}.title`)}</h4>
        <p className="wl-section-subtitle">{t(`${P}.subtitle`)}</p>
      </header>
      <div className="wl-section-body">
        <SummaryCard summary={summary} org={org} dirty={dirty} boardText={boardText} />

        {view.is_platform_root ? (
          <div className="wl-sb-notice" data-testid="wl-support-board-platform-root">
            <InfoCircleOutlined aria-hidden className="wl-sb-notice-icon" />
            <div>
              <div className="wl-sb-notice-title">{t(`${P}.rootTitle`)}</div>
              <div>{t(`${P}.platformRoot`)}</div>
            </div>
          </div>
        ) : (
          <div className="wl-sb-block">
            <div className="wl-sb-block-label" id={id('mode')}>{t(`${P}.sendTo`)}</div>
            <div className="wl-sb-cards" role="radiogroup" aria-labelledby={id('mode')} data-testid="wl-support-board-mode">
              {cards.map((card, i) => {
                const on = i === selected;
                return (
                  <button
                    key={card.testId}
                    ref={el => { cardRefs.current[i] = el; }}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    tabIndex={on ? 0 : -1}
                    className={`wl-sb-card${on ? ' is-on' : ''}`}
                    onClick={() => choose(i)}
                    onKeyDown={e => onCardKey(e, i)}
                    data-testid={`wl-support-board-card-${card.testId}`}
                  >
                    <span className="wl-sb-card-icon" aria-hidden>{card.icon}</span>
                    <span className="wl-sb-card-text">
                      <span className="wl-sb-card-title" dir="auto">{card.title}</span>
                      <span className="wl-sb-card-line">{card.line}</span>
                    </span>
                    <span className="wl-sb-card-mark" aria-hidden>{on && <CheckCircleFilled />}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {draft.mode === 'board' && !view.is_platform_root && (
          <MondayBoardSetup tenantId={tenantId} view={view} brandName={brandName} onView={applyView}
                            existingFields={existingFields} />
        )}

        {draft.mode === 'inherit' && view.inherited && !view.is_platform_root && (
          view.monday.owner_connection ? (
            <div className="wl-field-hint" data-testid="wl-support-board-via-owner">
              {t(`${P}.monday.viaOwner`, { org: bidiIsolate(view.inherited.source_tenant_name) })}
            </div>
          ) : (
            <div className="wl-sb-notice wl-sb-notice--warn" role="status" data-testid="wl-support-board-owner-unconnected">
              <ExclamationCircleFilled aria-hidden className="wl-sb-notice-icon" />
              <span>{t(`${P}.monday.ownerNotConnected`, { org: bidiIsolate(view.inherited.source_tenant_name) })}</span>
            </div>
          )
        )}

        {error && (
          <div className="wl-sb-link-state wl-sb-link-state--error" role="alert" data-testid="wl-support-board-error">
            <ExclamationCircleFilled aria-hidden />
            <span>{t(`${P}.errors.${error.code}`, { defaultValue: t(`${P}.errors.generic`) })}</span>
          </div>
        )}

        <div className="wl-sb-block" data-testid="wl-support-board-below">
          <div className="wl-sb-block-head">
            <span className="wl-sb-block-label">{t(`${P}.belowTitle`)}</span>
            {view.organizations_under > 0 && <span className="wl-sb-count">{view.organizations_under}</span>}
          </div>
          {view.organizations_under === 0 ? (
            <div className="wl-field-hint">{t(`${P}.belowNone`)}</div>
          ) : (<>
            {below.rows.length > 0 && (
              <ul className="wl-sb-orgs" data-testid="wl-support-board-orgs">
                {below.rows.map(row => (
                  <li key={row.tenant_id}>
                    <button type="button" className="wl-sb-org" onClick={() => openOrg(row.tenant_id)}
                            data-testid={`wl-support-board-org-${row.kind}`}>
                      <span className="wl-sb-org-name" dir="auto">{row.tenant_name}</span>
                      <span className={`wl-sb-pill wl-sb-pill--${row.kind}`}>
                        {row.kind === 'follows' && t(`${P}.below.follows`)}
                        {row.kind === 'off' && t(`${P}.below.off`)}
                        {row.kind === 'own' && <>{t(`${P}.below.own`)} <bdi>{boardText(row.board)}</bdi></>}
                      </span>
                      <Chevron aria-hidden className="wl-sb-org-chevron" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="wl-field-hint" data-testid="wl-support-board-below-rest">
              {below.rows.length === 0
                ? t(`${P}.below.allFollow`, { count: view.organizations_under })
                : below.others > 0
                  ? t(`${P}.below.others`, { count: below.others })
                  : null}
              {' '}{t(`${P}.below.howTo`)}
            </div>
          </>)}
        </div>

        <SupportDelivery tenantId={tenantId} view={view} draft={draft} dirty={dirty} onView={next => applyView(next)} />
      </div>
    </section>
  );
});

export default SupportBoardSection;

function SummaryCard({ summary, org, dirty, boardText }: {
  summary: Summary; org: string; dirty: boolean; boardText: (b: BoardChip) => string;
}) {
  const { t } = useLanguage();
  const values: Record<string, string> = { org: bidiIsolate(org) };
  let key: string;
  if (summary.kind === 'board') {
    values.board = bidiIsolate(boardText(summary.board));
    if (summary.setOn) values.source = bidiIsolate(summary.setOn);
    key = (summary.setOn ? 'boardInherited' : 'boardOwn') + (summary.notYet ? 'NotYet' : '');
  } else if (summary.kind === 'pending') {
    key = 'pending';
  } else {
    if (summary.offBy) values.offBy = bidiIsolate(summary.offBy);
    key = { off: 'off', off_above: 'offAbove', nothing_above: 'nothingAbove', top_level: 'topLevel',
            platform_root: 'platformRoot' }[summary.reason];
  }
  const tone = summary.kind === 'board' ? (summary.notYet ? 'pending' : 'board')
    : summary.kind === 'pending' ? 'pending' : 'none';
  return (
    <div className={`wl-sb-summary wl-sb-summary--${tone}`} data-testid="wl-support-board-summary" aria-live="polite">
      <div className="wl-sb-summary-head">
        <span className="wl-sb-summary-label">{t(`${P}.summary.label`)}</span>
        {dirty && <span className="wl-sb-unsaved" data-testid="wl-support-board-unsaved">{t(`${P}.summary.unsaved`)}</span>}
      </div>
      <p className="wl-sb-summary-text" data-testid={`wl-support-board-summary-${key}`}>
        <Trans i18nKey={`${P}.summary.${key}`} values={values} components={{ b: <strong /> }} />
      </p>
      {summary.kind === 'board' && <BoardChipView board={summary.board} label={boardText(summary.board)} />}
    </div>
  );
}

/** The board as a chip: a neutral board mark, its name or number, and a link out. */
function BoardChipView({ board, label }: { board: BoardChip; label: string }) {
  const { t } = useLanguage();
  const href = board.url ? safeHttpUrl(board.url) : null;
  return (
    <div className="wl-sb-chip" data-testid="wl-support-board-chip">
      <span className="wl-sb-chip-mark" aria-hidden><ProjectOutlined /></span>
      <span className="wl-sb-chip-text">
        <span className="wl-sb-chip-name"><bdi>{label}</bdi></span>
        <span className="wl-sb-chip-kind">{t(`${P}.chipKind`)}</span>
      </span>
      {href && (
        <a className="wl-sb-chip-open" href={href} target="_blank" rel="noopener noreferrer"
           data-testid="wl-support-board-open">
          {t(`${P}.openBoard`)} <ExportOutlined aria-hidden />
        </a>
      )}
    </div>
  );
}
